import { useEffect, useMemo } from "react";
import useSWR from "swr";
import { KCruxSensorProductType } from "@WahooFitness/wsm-native/dist/esm/types/kc/KCruxSensorProductType";
import { useWsmPlugin } from "./useWsmPlugin";
import usePairedDevices from "./usePairedDevices";
import useLocalStorage from "./useLocalStorage";

export const HAS_EVER_PAIRED_TYMEWEAR_KEY = "hasEverPairedTymewear";

// DEX-788: gate the ventilation zones menu item on the user having an ELEMNT
// with a paired Tymewear VitalPro Breathing remote sensor.
// DEX-873: the live check only passes while the ELEMNT is connected and
// reporting the sensor, so once it has passed we persist a flag and keep the
// menu item visible from then on.
const useHasElemntWithTymewear = () => {
  const { sensorManagerPlugin } = useWsmPlugin();
  const { devices } = usePairedDevices();
  const { data: hasEverPairedTymewear, setValue: setHasEverPairedTymewear } = useLocalStorage(
    HAS_EVER_PAIRED_TYMEWEAR_KEY
  );

  // boltType is only defined for ELEMNT bike computers.
  const elemntSensorIds = useMemo(
    () =>
      devices?.filter((device) => device.boltType !== undefined).map((device) => device.sensorId) ??
      [],
    [devices]
  );

  // The flag never unsets, so skip the live sensor check once it has been persisted.
  const { data: hasLivePairedTymewear } = useSWR(
    elemntSensorIds.length && hasEverPairedTymewear !== true
      ? ["elemntWithTymewear", elemntSensorIds.join(",")]
      : null,
    async () => {
      const results = await Promise.all(
        elemntSensorIds.map((sensorId) => sensorManagerPlugin?.getRemoteSensorStatus({ sensorId }))
      );
      return results.some((result) =>
        result?.result?.sensors?.some(
          (sensor) =>
            sensor.status.is_paired &&
            sensor.status.sensor_product_type === KCruxSensorProductType.VITALPRO_BREATHING
        )
      );
    }
  );

  useEffect(() => {
    if (hasLivePairedTymewear && hasEverPairedTymewear !== true) {
      setHasEverPairedTymewear(true);
    }
  }, [hasLivePairedTymewear, hasEverPairedTymewear, setHasEverPairedTymewear]);

  return !!hasLivePairedTymewear || hasEverPairedTymewear === true;
};

export default useHasElemntWithTymewear;
